'use client';

import { useMemo } from 'react';
import type { StepConfig, FormInstance, FormValues } from '../types';
import { findSectionForStep, findCardByGroupKey } from '../utils/sections';
import { getNextStepIdFromConfig } from '../utils/navigation';
import { getAddMoreConfig, isAddMoreEnabled } from '../utils/addMoreConfig';

const DEFAULT_MAX_INSTANCES = 5;

interface UseStepLogicProps {
  currentStep: StepConfig;
  currentStepId: string;
  currentSectionId: string | null;
  allSteps: StepConfig[];
  completedForms: Record<string, FormInstance[]>;
  values: FormValues;
  localStorageKey?: string;
}

interface UseStepLogicReturn {
  sectionInfo: ReturnType<typeof findSectionForStep> | null;
  canAddMore: boolean;
  isLastStepOfCard: boolean;
  isSectionStep: boolean;
  isLastStep: boolean;
}

/**
 * Hook to calculate step-related logic and flags
 */
export const useStepLogic = ({
  currentStep,
  currentStepId,
  currentSectionId,
  allSteps,
  completedForms,
  values,
}: UseStepLogicProps): UseStepLogicReturn => {
  // Check if current step is part of a card with maxInstances
  const sectionInfo = useMemo(() => {
    if (!currentSectionId) return null;
    return findSectionForStep(currentStepId, allSteps);
  }, [currentStepId, currentSectionId, allSteps]);

  const canAddMore = useMemo(() => {
    // Special handling for step-4-5-relationships: check children-group
    // This step can have addMore even if not part of a card
    if (currentStepId === 'step-4-5-relationships') {
      // Only allow addMore if hasChildren is "yes"
      if (values.hasChildren !== 'Yes') return false;
      const childrenCard = findCardByGroupKey('children-group', allSteps);
      const maxChildren = childrenCard?.card?.maxInstances ?? DEFAULT_MAX_INSTANCES;
      const completedChildren = completedForms['children-group'] || [];
      return completedChildren.length < maxChildren;
    }

    // Special handling for addMore steps that might not have card context set
    // Check if current step is an addMore step
    const addMoreConfig = getAddMoreConfig(currentStep);
    if (addMoreConfig && isAddMoreEnabled(currentStep, values)) {
      // Try to find the card from the section
      let card = sectionInfo?.card;

      if (card && card?.groupKey !== addMoreConfig.groupKey) {
        const cardResult = findCardByGroupKey(addMoreConfig.groupKey, allSteps);
        card = cardResult?.card;
      }
      // If card not found via sectionInfo, try to find it by searching all steps
      if (!card && addMoreConfig.groupKey) {
        const cardResult = findCardByGroupKey(addMoreConfig.groupKey, allSteps);
        card = cardResult?.card;
      }

      // If we found a card with maxInstances, use it
      if (card && card.maxInstances !== undefined) {
        const currentInstances = completedForms[addMoreConfig.groupKey] || [];
        return currentInstances.length < card.maxInstances;
      }

      // If no card found or no maxInstances, use default
      const currentInstances = completedForms[addMoreConfig.groupKey] || [];
      return currentInstances.length < DEFAULT_MAX_INSTANCES;
    }

    // For card-based steps
    if (!sectionInfo?.card) return false;
    if (!sectionInfo.card.maxInstances) return false;
    const groupKey = sectionInfo.card.groupKey;
    const currentInstances = completedForms[groupKey] || [];
    return currentInstances.length < sectionInfo.card.maxInstances;
  }, [sectionInfo, completedForms, currentStepId, values, allSteps, currentStep]);

  const isLastStepOfCard = useMemo(() => {
    if (!sectionInfo?.card) return false;
    const stepIndex = sectionInfo.stepIndex;
    return stepIndex === sectionInfo.card.stepIds.length - 1;
  }, [sectionInfo]);

  const isSectionStep = useMemo(() => {
    return Boolean(currentStep.sections && currentStep.sections.length > 0);
  }, [currentStep]);

  const isLastStep = useMemo(() => {
    // Check if next step type is submit
    if (currentStep.next?.type === 'submit') return true;
    // Check if next step ID is 'step-9-summary'
    const nextStepId = getNextStepIdFromConfig(currentStep, values);
    if (nextStepId === 'step-10-thankyou') return true;
    return false;
  }, [currentStep, values]);

  return {
    sectionInfo,
    canAddMore,
    isLastStepOfCard,
    isSectionStep,
    isLastStep,
  };
};
